/**
 * Exportação de CSV compartilhada entre ImoTable e ImoTableV2.
 *
 * Faz o scrape da PRÓPRIA tabela renderizada (via `tableRef`) — nunca varre
 * outras tabelas do documento (ex.: sidepanels/modais), evitando duplicação.
 * Cada célula é normalizada (quebras de linha viram espaço, aspas escapadas) e
 * o arquivo é nomeado `<prefixo>_csv_<datahora local>.csv` (prefixo = fileName
 * ou "tabela").
 *
 * Observação: exporta a página renderizada. A exportação de todas as páginas é
 * responsabilidade do backend (report assíncrono) — ver PR de export no backend.
 *
 * @param {import('vue').Ref<HTMLTableElement|null>} tableRef
 * @param {() => string} [getFileName] - prefixo do arquivo (csvFileName)
 * @returns {{ createCsv: () => void }}
 */
export function useTableCsv(tableRef, getFileName) {
  const cell = value => {
    const text = value == null ? '' : String(value).replace(/\s+/g, ' ').trim()
    return `"${text.replace(/"/g, '""')}"`
  }

  const scrapeDom = () => {
    const rows = tableRef.value?.querySelectorAll('tr') ?? []
    return Array.from(rows)
      .map(row =>
        Array.from(row.querySelectorAll('td, th'))
          .map(col => cell(col.innerText))
          .join(','),
      )
      .join('\n')
  }

  const download = csv => {
    // Timestamp em horário local, formato YYYY-MM-DD_HH-MM-SS (seguro p/ nome de arquivo).
    const d = new Date()
    const pad = n => String(n).padStart(2, '0')
    const stamp =
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`
    const prefix = getFileName?.() || 'tabela'

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.download = `${prefix}_csv_${stamp}.csv`
    link.href = url
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  const createCsv = () => {
    download(scrapeDom())
  }

  return { createCsv }
}
